import React, { memo } from 'react';

import clsx from 'clsx';
import {
  RecordContextProvider,
  useRecordContext,
  useResourceContext,
  DatagridCell,
  DatagridClasses,
} from 'react-admin';
import { Box } from 'shared/mui/Box';
import { TableCell, TableRow } from 'shared/mui/Table';
import { Typography } from 'shared/mui/Typography';
import { DatagridRowProps } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';

type Props = Omit<DatagridRowProps, 'component' | 'sx'>;

const BankOperationsPureRow: React.ForwardRefExoticComponent<
  Omit<DatagridRowProps, 'ref' | 'sx'> & React.RefAttributes<HTMLTableRowElement>
> = React.forwardRef<HTMLTableRowElement, Props>((props, ref) => {
  const { children, className, hover = true, id, style, selectable = true, ...rest } = props;

  const record = useRecordContext(props);
  const resource = useResourceContext(props);

  return (
    <RecordContextProvider value={record}>
      <TableRow
        className={clsx(className, {
          [DatagridClasses.selectable]: selectable,
        })}
        hover={hover}
        key={id}
        ref={ref}
        style={style}
        {...rest}
      >
        {React.Children.map(children, (field: any, index) => {
          if (!field) {
            return null;
          }

          return (
            <DatagridCell
              className={clsx(`column-${field.props.source}`, DatagridClasses.rowCell)}
              key={index}
              record={record}
              {...{ field, resource }}
            />
          );
        })}
      </TableRow>
      {!!record?.related && (
        <RecordContextProvider value={record?.related}>
          <TableRow
            className={clsx(className)}
            hover={hover}
            key={id}
            ref={ref}
            {...rest}
            sx={{
              backgroundColor: 'tableHead',
            }}
          >
            <TableCell
              className={clsx('column-originId', 'resize-originId', DatagridClasses.rowCell)}
            >
              <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
                <Typography color="primary">&#10149;</Typography>
                {record?.related?.id}
              </Box>
            </TableCell>
            {React.Children.map(children, (field: any, index) => {
              if (field.props.source === 'originId' || !field.props?.label) {
                return <></>;
              }
              return (
                <DatagridCell
                  className={clsx(`column-${field.props.source}`, DatagridClasses.rowCell)}
                  key={index}
                  record={record?.related}
                  {...{ field, resource }}
                />
              );
            })}
            <TableCell key={13} />
          </TableRow>
        </RecordContextProvider>
      )}
    </RecordContextProvider>
  );
});

export const BankOperationRow = memo(BankOperationsPureRow);

BankOperationsPureRow.displayName = 'BankOperationRow';
